import React, { useState, useEffect } from 'react';
import { Heart, Calendar, ChevronDown, Trophy, Users, Loader2, Star, ArrowUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { eventApi, matchApi, userInterestsApi } from '../lib/api';

interface ContactInfo {
  id: string;
  name: string;
  gender: string;
  birthdate: string;
}

interface MatchEntry {
  id: string;
  participant1: ContactInfo | null;
  participant2: ContactInfo | null;
  mutual: boolean;
  status: string;
  source: string;
}

interface RankingEntry {
  contact: ContactInfo;
  receivedCount: number;
  givenCount: number;
}

type SortField = 'received' | 'given' | 'name';
type SortDirection = 'asc' | 'desc';

interface SpeedDateMatchesProps {
  onStatsChange: (stats: { eventsCount: number; matchesCount: number; participantsCount: number; totalPreferences: number }) => void;
}

const SpeedDateMatches = ({ onStatsChange }: SpeedDateMatchesProps) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [matches, setMatches] = useState<MatchEntry[]>([]);
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'matches' | 'ranking'>('matches');
  const [sortField, setSortField] = useState<SortField>('received');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadEventData(selectedEventId);
    } else {
      setMatches([]);
      setRanking([]);
    }
  }, [selectedEventId]);

  useEffect(() => {
    onStatsChange({
      eventsCount: events.length,
      matchesCount: matches.length,
      participantsCount: ranking.length,
      totalPreferences: ranking.reduce((acc, r) => acc + r.givenCount, 0),
    });
  }, [events.length, matches.length, ranking]);

  const loadEvents = async () => {
    try {
      setLoadingEvents(true);
      const eventsData = await eventApi.getAll();
      const closedEvents = eventsData
        .filter((event: any) => event.matches_sent === true)
        .filter((event: any) =>
          user?.role === 'admin' || event.city === user?.city
        )
        .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setEvents(closedEvents);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento degli eventi');
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEventData = async (eventId: string) => {
    try {
      setLoadingData(true);
      setError(null);
      const [matchesData, interestsData] = await Promise.all([
        matchApi.getByEventId(eventId),
        userInterestsApi.getByEventId(eventId)
      ]);

      const heartlinkMatches = (matchesData || []).filter(
        (m: any) => m.source === 'heartlink' && m.mutual === true
      );
      setMatches(heartlinkMatches);

      const receivedMap = new Map<string, number>();
      const givenMap = new Map<string, number>();
      const contactMap = new Map<string, ContactInfo>();

      (interestsData || []).forEach((interest: any) => {
        if (interest.to_user) {
          const toId = interest.to_user.id;
          contactMap.set(toId, interest.to_user);
          receivedMap.set(toId, (receivedMap.get(toId) || 0) + 1);
        }
        if (interest.from_user) {
          const fromId = interest.from_user.id;
          contactMap.set(fromId, interest.from_user);
          givenMap.set(fromId, (givenMap.get(fromId) || 0) + 1);
        }
      });

      const rankingList: RankingEntry[] = Array.from(contactMap.entries()).map(([id, contact]) => ({
        contact,
        receivedCount: receivedMap.get(id) || 0,
        givenCount: givenMap.get(id) || 0,
      }));

      rankingList.sort((a, b) => b.receivedCount - a.receivedCount);
      setRanking(rankingList);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      setLoadingData(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getGenderLabel = (gender: string) => {
    const labels: Record<string, string> = {
      'uomo_etero': 'Uomo',
      'donna_etero': 'Donna',
      'uomo_gay': 'Uomo',
      'donna_gay': 'Donna',
      'non_dichiarato': 'N/D'
    };
    return labels[gender] || gender;
  };

  const getGenderColor = (gender: string) => {
    if (gender?.startsWith('uomo')) return 'bg-sky-100 text-sky-700';
    if (gender?.startsWith('donna')) return 'bg-rose-100 text-rose-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getMedalColor = (position: number) => {
    if (position === 0) return 'text-amber-500';
    if (position === 1) return 'text-gray-400';
    if (position === 2) return 'text-amber-700';
    return 'text-gray-300';
  };

  const sortedRanking = [...ranking].sort((a, b) => {
    const dir = sortDirection === 'desc' ? -1 : 1;
    if (sortField === 'received') return (b.receivedCount - a.receivedCount) * dir;
    if (sortField === 'given') return (b.givenCount - a.givenCount) * dir;
    return a.contact.name.localeCompare(b.contact.name) * dir;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div>
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:sr-only">Seleziona Evento</label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all"
              >
                <option value="">-- Seleziona un evento --</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name} - {formatDate(event.date)} ({event.city})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 sm:top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setActiveView('matches')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeView === 'matches'
                    ? 'bg-rose-500 text-white shadow-md shadow-rose-200'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Heart className="h-4 w-4" />
                Match {selectedEventId ? `(${matches.length})` : ''}
              </button>
              <button
                onClick={() => setActiveView('ranking')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeView === 'ranking'
                    ? 'bg-rose-500 text-white shadow-md shadow-rose-200'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Trophy className="h-4 w-4" />
                <span className="hidden sm:inline">Classifica Preferenze</span>
                <span className="sm:hidden">Classifica</span>
                {selectedEventId ? ` (${ranking.length})` : ''}
              </button>
            </div>
          </div>
          {loadingEvents && (
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Caricamento eventi...
            </div>
          )}
        </div>
      </div>

      {selectedEventId && (
        <>
          {loadingData ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
              <div className="flex flex-col items-center justify-center text-gray-500">
                <Loader2 className="h-8 w-8 animate-spin mb-3 text-rose-400" />
                <p className="text-sm">Caricamento dati...</p>
              </div>
            </div>
          ) : activeView === 'matches' ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 sm:p-5 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-rose-500" />
                  Match di {selectedEvent?.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Match reciproci generati automaticamente</p>
              </div>
              {matches.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {matches.map((match, index) => (
                    <div
                      key={match.id}
                      className="flex items-center gap-4 p-4 sm:px-5 hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center">
                        <span className="text-sm font-semibold text-rose-500">{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900">{match.participant1?.name || 'N/A'}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getGenderColor(match.participant1?.gender || '')}`}>
                            {getGenderLabel(match.participant1?.gender || '')}
                          </span>
                          <Heart className="h-4 w-4 text-rose-400 fill-rose-400 flex-shrink-0" />
                          <span className="font-medium text-gray-900">{match.participant2?.name || 'N/A'}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getGenderColor(match.participant2?.gender || '')}`}>
                            {getGenderLabel(match.participant2?.gender || '')}
                          </span>
                        </div>
                      </div>
                      <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Match
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Heart className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-500 text-sm">Nessun match trovato per questo evento</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-4 sm:p-5 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Classifica Preferenze - {selectedEvent?.name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">Ordinata per numero di preferenze ricevute</p>
              </div>
              {sortedRanking.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 sm:px-5 py-3 w-12">#</th>
                        <th
                          className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 sm:px-5 py-3 cursor-pointer select-none hover:text-gray-700"
                          onClick={() => handleSort('name')}
                        >
                          <span className="flex items-center gap-1">
                            Partecipante
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </span>
                        </th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 sm:px-5 py-3">Genere</th>
                        <th
                          className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 sm:px-5 py-3 cursor-pointer select-none hover:text-gray-700"
                          onClick={() => handleSort('received')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Ricevute
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </span>
                        </th>
                        <th
                          className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-4 sm:px-5 py-3 cursor-pointer select-none hover:text-gray-700"
                          onClick={() => handleSort('given')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            Date
                            <ArrowUpDown className="h-3.5 w-3.5" />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {sortedRanking.map((entry, index) => (
                        <tr key={entry.contact.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 sm:px-5 py-3">
                            {index < 3 && sortField === 'received' && sortDirection === 'desc' ? (
                              <Trophy className={`h-5 w-5 ${getMedalColor(index)}`} />
                            ) : (
                              <span className="text-sm text-gray-400 font-medium">{index + 1}</span>
                            )}
                          </td>
                          <td className="px-4 sm:px-5 py-3">
                            <span className="font-medium text-gray-900">{entry.contact.name}</span>
                          </td>
                          <td className="px-4 sm:px-5 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getGenderColor(entry.contact.gender)}`}>
                              {getGenderLabel(entry.contact.gender)}
                            </span>
                          </td>
                          <td className="px-4 sm:px-5 py-3 text-center">
                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600">
                              <Star className="h-3.5 w-3.5 fill-rose-400 text-rose-400" />
                              {entry.receivedCount}
                            </span>
                          </td>
                          <td className="px-4 sm:px-5 py-3 text-center">
                            <span className="text-sm text-gray-600">{entry.givenCount}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-500 text-sm">Nessuna preferenza trovata per questo evento</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!selectedEventId && !loadingEvents && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-200" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Seleziona un evento</h3>
          <p className="text-sm text-gray-500">
            Scegli un evento per visualizzare i dati
          </p>
        </div>
      )}
    </div>
  );
};

export default SpeedDateMatches;
